import type { FocusType, PackageType, Role, WorkspaceStatus } from "../types";

export interface ProvisionDepartmentInput {
  name?: string;
  contributorEmail?: string;
}

export interface ProvisionWorkspaceInput {
  organizationName?: string;
  organizationIndustry?: string;
  organizationCompanySize?: string;
  workspaceName?: string;
  packageType?: PackageType;
  startDate?: string;
  endDate?: string;
  status?: WorkspaceStatus;
  goal?: string;
  focusType?: FocusType;
  maxDepartments?: number;
  ownerEmail?: string;
  ownerName?: string;
  departments?: ProvisionDepartmentInput[];
  sendSetupEmails?: boolean;
}

export interface NormalizedProvisionDepartment {
  name: string;
  contributorEmail?: string;
}

export interface NormalizedProvisionWorkspaceInput {
  organizationName: string;
  organizationIndustry?: string;
  organizationCompanySize?: string;
  workspaceName: string;
  packageType: PackageType;
  startDate: string;
  endDate: string;
  status: WorkspaceStatus;
  goal?: string;
  focusType: FocusType;
  maxDepartments: number;
  ownerEmail: string;
  ownerName?: string;
  departments: NormalizedProvisionDepartment[];
  sendSetupEmails: boolean;
}

const validPackages = new Set<PackageType>([
  "guided_digital_access_3m",
  "guided_digital_access_plus_6m",
  "guided_digital_access_executive_12m",
  "monthly_subscription"
]);

const validStatuses = new Set<WorkspaceStatus>(["draft", "active", "paused", "locked", "expired", "archived"]);
const validFocusTypes = new Set<FocusType>(["existing_customers", "new_customers", "both"]);

function normalizeText(value: unknown): string | undefined {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized || undefined;
}

function normalizeEmail(value: unknown): string | undefined {
  const normalized = normalizeText(value)?.toLowerCase();
  return normalized || undefined;
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00.000Z`).getTime());
}

export function validateProvisionWorkspaceInput(
  input: ProvisionWorkspaceInput
): { ok: true; value: NormalizedProvisionWorkspaceInput } | { ok: false; message: string } {
  const organizationName = normalizeText(input.organizationName);
  const workspaceName = normalizeText(input.workspaceName);
  const ownerEmail = normalizeEmail(input.ownerEmail);
  const packageType = input.packageType;
  const startDate = normalizeText(input.startDate);
  const endDate = normalizeText(input.endDate);
  const status = input.status ?? "draft";
  const focusType = input.focusType ?? "both";
  const maxDepartments = Number(input.maxDepartments ?? 0);

  if (!organizationName) return { ok: false, message: "Organization Name ist erforderlich." };
  if (!workspaceName) return { ok: false, message: "Workspace Name ist erforderlich." };
  if (!packageType || !validPackages.has(packageType)) return { ok: false, message: "Bitte ein gültiges Paket auswählen." };
  if (!startDate || !isDate(startDate)) return { ok: false, message: "Bitte ein gültiges Startdatum eintragen." };
  if (!endDate || !isDate(endDate)) return { ok: false, message: "Bitte ein gültiges Enddatum eintragen." };
  if (new Date(`${endDate}T00:00:00.000Z`).getTime() < new Date(`${startDate}T00:00:00.000Z`).getTime()) {
    return { ok: false, message: "Das Enddatum darf nicht vor dem Startdatum liegen." };
  }
  if (!validStatuses.has(status)) return { ok: false, message: "Bitte einen gültigen Workspace-Status auswählen." };
  if (!validFocusTypes.has(focusType)) return { ok: false, message: "Bitte einen gültigen Fokus auswählen." };
  if (!Number.isInteger(maxDepartments) || maxDepartments < 1 || maxDepartments > 10) {
    return { ok: false, message: "Max Departments muss zwischen 1 und 10 liegen." };
  }
  if (!ownerEmail || !isEmail(ownerEmail)) return { ok: false, message: "Bitte eine gültige Company Owner E-Mail eintragen." };

  const departments = (input.departments ?? [])
    .map((department) => ({
      name: normalizeText(department.name),
      contributorEmail: normalizeEmail(department.contributorEmail)
    }))
    .filter((department) => department.name || department.contributorEmail);

  if (departments.length === 0) return { ok: false, message: "Bitte mindestens eine Abteilung anlegen." };
  if (departments.length > maxDepartments) {
    return { ok: false, message: "Die Anzahl der Abteilungen überschreitet Max Departments." };
  }

  const invalidDepartment = departments.find((department) => !department.name);
  if (invalidDepartment) return { ok: false, message: "Jede Abteilung braucht einen Namen." };
  const invalidContributor = departments.find(
    (department) => department.contributorEmail && !isEmail(department.contributorEmail)
  );
  if (invalidContributor) return { ok: false, message: "Bitte gültige Contributor E-Mail-Adressen eintragen." };

  return {
    ok: true,
    value: {
      organizationName,
      organizationIndustry: normalizeText(input.organizationIndustry),
      organizationCompanySize: normalizeText(input.organizationCompanySize),
      workspaceName,
      packageType,
      startDate,
      endDate,
      status,
      goal: normalizeText(input.goal),
      focusType,
      maxDepartments,
      ownerEmail,
      ownerName: normalizeText(input.ownerName),
      departments: departments as NormalizedProvisionDepartment[],
      sendSetupEmails: Boolean(input.sendSetupEmails)
    }
  };
}

export function getProvisionedOwnerRole(existingRole?: Role): Role {
  return existingRole === "hauffe_admin" ? "hauffe_admin" : "company_owner";
}

export function shouldUpdateExistingOwnerRole(existingRole?: Role): boolean {
  return Boolean(existingRole && existingRole !== "hauffe_admin" && existingRole !== "company_owner");
}
